import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppStore } from '@store/appStore';
import { apiClient } from '@/lib/api';
import { ROUTES } from '@/constants';
import { Skeleton } from '@components/ui/Skeleton';
import { formatUSPhone } from '../utils';
import AddPassengerDrawer from '@/features/trip-sheet/components/AddPassengerDrawer';
import QuoteDetailsDrawer from '@/features/quotes/components/QuoteDetailsDrawer';
import AdvanceInvoiceDrawer from '@/features/quotes/components/AdvanceInvoiceDrawer';
import { getQuoteStatusConfig, getEffectiveQuoteStatus, selectPrimaryBidOption } from '@/features/quotes/utils';
import { formatUTCToDateTime } from '@/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { getOperatorWithLocation } from '@/features/tails/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS } from '@/lib/permissions';
import {
  RiContactsBook2Line,
  RiBuildingLine,
  RiMailLine,
  RiPhoneLine,
  RiListCheck3,
  RiTeamLine,
  RiTimeLine,
  RiUserAddLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightSLine,
  RiCalendarLine,
  RiFileList3Line
} from '@remixicon/react';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const initialClient = location.state?.client || null;

  // Edit requires Clients module update access — hidden for
  // flight_coordinator, finance, compliance_qa.
  const { can, permission } = usePermissions();
  const canEditClient = can(MODULES.CLIENTS, ACTIONS.UPDATE);
  const hasPartialClientsRead = !!permission(MODULES.CLIENTS).partial;

  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [quoteDrawerOpen, setQuoteDrawerOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [selectedInvoiceQuote, setSelectedInvoiceQuote] = useState(null);

  const { data: client, isLoading, isFetching: isClientFetching } = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiClient.getClientById(id, { include: 'quotes,quotes.bids' }),
    enabled: !!id,
    initialData: initialClient,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: passengersData = [], isLoading: isPassengersLoading } = useQuery({
    queryKey: ['clientPassengers', id],
    queryFn: () => apiClient.getClientPassengers(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const name = client ? [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Unknown' : '';
  const company = client?.company_name || client?.company || '';

  // Set page header in AppLayout. Only attach onEdit when the role can update
  // clients — AppLayout hides the Edit button when no callback is provided.
  useEffect(() => {
    if (client) {
      setPageHeaderOverride({
        title: name,
        subtitle: company || undefined,
        ...(canEditClient
          ? { onEdit: () => navigate(`/clients/${id}/edit`, { state: { client } }) }
          : {}),
      });
    }
    return () => clearPageHeaderOverride();
  }, [client, name, company, id, navigate, setPageHeaderOverride, clearPageHeaderOverride, canEditClient]);
  const email = client?.email || '';
  const phone = client?.contact_numbers?.[0] || client?.phone || '';
  const operationalPreferences = client?.operational_preferences || '';
  const passengers = Array.isArray(passengersData) ? passengersData : (passengersData?.data || []);
  const { data: invoices = [] } = useQuery({
    queryKey: ['invoices', id],
    queryFn: () => apiClient.getInvoices({ client_id: id, include: 'items' }),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const quotes = client?.quotes || [];
  const mergedItems = [
    ...quotes.map(q => ({ ...q, _type: 'quote' })),
    ...invoices.map(inv => ({ ...inv, _type: 'invoice', quote_id: inv.invoice_id, quote_number: inv.invoice_number, total_due: inv.total_amount })),
  ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  
  console.log("mergedItems", mergedItems);

  const isQuotesLoading = isClientFetching && mergedItems.length === 0;

  const renameWithClientId = (clientId, file) => {
    const client = clientId || 'unknown';
    const newName = `${client}_${file.name}`;
    return new File([file], newName, { type: file.type });
  };

  const handleAddPassenger = async (passenger) => {
    try {
      const clientId = client.client_id || client.id;

      let passport_url = passenger.existingPassportUrl || null;
      let visa_url = passenger.existingVisaUrl || null;
      if (passenger.passportFiles?.length) {
        const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, passenger.passportFiles[0]), "passport");
        passport_url = uploaded?.file_path || null;
      }
      if (passenger.visaFiles?.length) {
        const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, passenger.visaFiles[0]), "visa");
        visa_url = uploaded?.file_path || null;
      }

      const dob = passenger.dateOfBirth
        ? passenger.dateOfBirth.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$1-$2')
        : null;

      const mapped = {
        full_name: passenger.fullName || '',
        date_of_birth: dob,
        nationality: passenger.nationality || null,
        passport_num: null,
        passport_url,
        visa_url,
        weight: passenger.weight || null,
        additional_info: {
          dietary_catering: passenger.dietaryCatering || null,
          cabin_environment: passenger.cabinEnvironment || null,
          medical_mobility: passenger.medicalMobility || null,
          pet_accommodations: passenger.petAccommodations || null,
        },
      };

      const passengerId = editingPassenger?.client_passenger_id || editingPassenger?.id;
      if (passengerId) {
        await apiClient.updateClientPassenger(passengerId, mapped);
        toast.success('Passenger updated successfully');
      } else {
        await apiClient.createClientPassengersBatch(clientId, [mapped]);
        toast.success('Passenger added successfully');
      }

      queryClient.invalidateQueries({ queryKey: ['clientPassengers', id] });
      setPassengerDrawerOpen(false);
      setEditingPassenger(null);
    } catch (err) {
      console.error('Failed to save passenger:', err);
      toast.error('Failed to save passenger');
    }
  };

  const handleRemovePassenger = async () => {
    try {
      const passengerId = editingPassenger?.client_passenger_id || editingPassenger?.id;
      if (passengerId) {
        await apiClient.deleteClientPassenger(passengerId);
        toast.success('Passenger removed successfully');
        queryClient.invalidateQueries({ queryKey: ['clientPassengers', id] });
        setEditingPassenger(null);
        setPassengerDrawerOpen(false);
      }
    } catch (err) {
      console.error('Failed to remove passenger:', err);
      toast.error('Failed to remove passenger');
      setEditingPassenger(null);
      setPassengerDrawerOpen(false);
    }
  };

  if (isLoading && !initialClient) {
    return (
      <div className="pb-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Client not found</p>
        <button
          onClick={() => navigate(ROUTES.CLIENTS.path)}
          className="mt-4 text-primary-600 text-sm font-medium"
        >
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="pb-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
      {/* Left Column: Contact Details + Operational Preferences */}
      <div className="flex flex-col gap-4 h-[calc(100vh-140px)]">
        {/* Contact Details */}
        <div className="bg-white rounded-2xl overflow-hidden shrink-0">
          <div className="flex items-center gap-3 p-5 pb-3 border-b boder-gray-20 mb-2">
            <RiContactsBook2Line className="w-5 h-5 text-gray-500" />
            <span className="text-md font-medium text-gray-900">Contact Details</span>
          </div>
          <div className="px-5 pb-5 space-y-4">
            <h3 className="text-2xl font-semibold text-gray-900">{name}</h3>
            {company && (
              <div className="flex items-start gap-3">
                <div className="bg-gray-100 p-2 rounded-full">
                <RiBuildingLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Company</p>
                  <p className="text-md text-gray-900 font-medium">{company}</p>
                </div>
              </div>
            )}
            {email && (
              <div className="flex items-start gap-3">
                <div className="bg-gray-100 p-2 rounded-full">
                <RiMailLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-md text-gray-900 font-medium">{email}</p>
                </div>
              </div>
            )}
            {phone && (
              <div className="flex items-start gap-3">
                <div className="bg-gray-100 p-2 rounded-full">
                <RiPhoneLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Phone</p>
                  <p className="text-md text-gray-900 font-medium">{formatUSPhone(phone)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Operational Preferences — hidden for roles with partial read on Clients (e.g. finance) */}
        {!hasPartialClientsRead && (
          <div className="bg-white rounded-2xl border border-[#EBEBEB] overflow-hidden flex flex-col flex-1">
            <div className="flex items-center gap-3 p-5 pb-3 border-b boder-gray-20 mb-2 shrink-0">
              <RiListCheck3 className="w-5 h-5 text-gray-500" />
              <span className="text-md font-medium text-gray-900">Operational Preferences</span>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {operationalPreferences ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{operationalPreferences}</p>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  Add client's operational preferences by{'\n'}tapping the edit button at the top right corner.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Middle Column: Saved Passengers */}
      <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden flex flex-col h-[calc(100vh-140px)]">
        <div className="flex items-center justify-between p-5 pb-3 border-b boder-[#EBEBEB] mb-2 shrink-0">
          <div className="flex items-center gap-3">
            <RiTeamLine className="w-5 h-5 text-gray-500" />
            <span className="text-md font-medium text-gray-900">Saved Passengers</span>
          </div>
          {canEditClient && (
            <button
              onClick={() => { setEditingPassenger(null); setPassengerDrawerOpen(true); }}
              className="flex items-center gap-1.5 py-1.5 px-3 bg-accent text-xs font-medium text-primary-500 rounded-full transition-colors"
            >
              <RiUserAddLine className="w-3.5 h-3.5" />
              Add Passenger
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5">
          {isPassengersLoading ? (
            <div className="space-y-2 mb-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32 rounded" />
                    <Skeleton className="h-3 w-20 rounded" />
                  </div>
                  <Skeleton className="h-4 w-8 rounded" />
                </div>
              ))}
            </div>
          ) : passengers.length > 0 ? (
            <div className="space-y-2 mb-4">
              {passengers.map((p, i) => (
                <div
                  key={p.id || i}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ')}
                    </p>
                    {p.nationality && (
                      <p className="text-xs text-gray-400">{p.nationality}</p>
                    )}
                  </div>
                  {canEditClient && (
                    <button
                      onClick={() => {
                        setEditingPassenger(p);
                        setPassengerDrawerOpen(true);
                      }}
                      className="text-xs text-primary-600 font-medium"
                    >
                      Edit
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4 mb-4">
              No passengers added yet
            </p>
          )}
        </div>
      </div>

      {/* Right Column: Quote History */}
      <div className="bg-white rounded-2xl border border-[#EBEBEB] overflow-hidden flex flex-col h-[calc(100vh-140px)]">
        <div className="flex items-center justify-between p-5 pb-3 border-b boder-[#EBEBEB] mb-2 shrink-0">
          <div className="flex items-center gap-3">
            <RiTimeLine className="w-5 h-5 text-gray-500" />
            <span className="text-md font-medium text-gray-900">Quote History</span>
          </div>
          {canEditClient && (
            <button
              onClick={() => navigate(`/clients/${client?.client_id || client?.id}/invoice`, { state: { clientName: name } })}
              className="flex items-center gap-1.5 py-1.5 px-3 bg-accent text-xs font-medium text-primary-500 rounded-full transition-colors"
            >
              <RiFileList3Line className="w-3.5 h-3.5" />
              Add Invoice
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {isQuotesLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32 rounded" />
                    <Skeleton className="h-3 w-20 rounded" />
                  </div>
                  <Skeleton className="h-4 w-16 rounded" />
                </div>
              ))}
            </div>
          ) : mergedItems.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {mergedItems.map((q, i) => {
                const isInvoice = !!q.invoice_id;
                const statx = isInvoice && q.status === 'SENT' ? 'PENDING_PAYMENT' : q.status;
                // Route through the promotion helper so PASSENGERS_PENDING
                // reads as "Trip Sheet In Progress" once the client has
                // submitted passenger details on the quote.
                const statusConfig = getQuoteStatusConfig(getEffectiveQuoteStatus(q, statx));
                const StatusIcon = statusConfig.icon;
                const title = q.departure_airport && q.arrival_airport
                  ? `${q.departure_airport} – ${q.arrival_airport}`
                  : q.invoice_number || q.quote_number || q.invoice_id?.slice(-8)?.toUpperCase() || q.quote_id?.slice(-8)?.toUpperCase() || '-';
                // Prefer ACCEPTED, else shortlisted, else the first
                // non-REJECTED option — skips soft-deleted rows that
                // would surface the wrong operator on the client's
                // recent-quotes row.
                const option = selectPrimaryBidOption(q.bid_items?.[0]?.bid_options);
                const subtitle = isInvoice
                  ? (q.invoice_type ? q.invoice_type.charAt(0) + q.invoice_type.slice(1).toLowerCase() + ' Invoice' : 'Invoice')
                  : getOperatorWithLocation(option?.operator);
                return (
                  <div
                    key={q.invoice_id || q.quote_id || i}
                    className="py-3 flex items-end gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      if (!q.invoice_id) {
                        setSelectedQuoteId(q.quote_id);
                        setQuoteDrawerOpen(true);
                      } else {
                        setSelectedInvoiceQuote({ ...q, client: { first_name: client?.first_name, last_name: client?.last_name } });
                        setInvoiceDrawerOpen(true);
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
                        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md ${statusConfig.className}`}>
                          {StatusIcon && <StatusIcon className="w-3 h-3" />}
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-0.5">
                        <RiTeamLine className="w-3 h-3 shrink-0" />
                        <span className="truncate">{name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <RiFileList3Line className="w-3 h-3 shrink-0" />
                        <span className="truncate">{subtitle}</span>
                      </div>
                    </div>
                    <RiArrowRightSLine className="w-4 h-4 text-gray-300 shrink-0 mb-0.5" />
                  </div>
                );
              })}
              <p className="text-xs text-gray-400 text-center pt-3">
                Showing all {mergedItems.length} {mergedItems.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              No quotes or invoices yet for {name}
            </p>
          )}
        </div>


      </div>

      {/* Quote Details Drawer */}
      <QuoteDetailsDrawer
        isOpen={quoteDrawerOpen}
        onOpenChange={setQuoteDrawerOpen}
        quoteId={selectedQuoteId}
      />
      <AdvanceInvoiceDrawer
        isOpen={invoiceDrawerOpen}
        onClose={() => setInvoiceDrawerOpen(false)}
        invoice={selectedInvoiceQuote}
      />

      {/* Add Passenger Drawer */}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => {
          setPassengerDrawerOpen(false);
          setEditingPassenger(null);
        }}
        onAdd={handleAddPassenger}
        onRemove={handleRemovePassenger}
        editingPassenger={editingPassenger}
        clientId={client.client_id || client.id}
        newOnly
      />
    </div>
  );
};

export default ClientDetail;
